/**
 * The share-platform surfaces, shared by the company/profile share sheet and
 * the CV share sheet.
 *
 * Two exports:
 *  - `SharePlatformRail`  — the entry point in a sheet's main view: a label plus
 *                           a horizontally scrolling row of platform tiles.
 *  - `AddToBioGuide`      — the detail view: notice, numbered steps, the link to
 *                           copy, a button through to the platform, and a
 *                           "post about it" action where one exists.
 *
 * Styling deliberately reuses the CV share sheet's vocabulary (16px `linkCard`,
 * `var(--hover)` tiles, the 56px `var(--text-primary)` primary bar) so the two
 * sheets read as one feature rather than two designs.
 */
import { useCallback, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { ProfileBrandIcon } from './ProfileIconRegistry';
import {
  getLinkInBioPlatforms,
  readSteps,
  type LinkInBioPlatform,
  type LinkInBioPlatformId,
} from '../../lib/link-in-bio';

/** Splits `https://www.tabbio.com/acme` into `tabbio.com/acme` for display. */
function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function PlatformGlyph({
  platform,
  size = 20,
}: {
  platform: LinkInBioPlatform;
  size?: number;
}) {
  if (platform.icon) {
    return <ProfileBrandIcon id={platform.icon} size={size} color="currentColor" />;
  }
  return <MaterialSymbol name={platform.symbol ?? 'link'} size={size} />;
}

// ─── Rail (main view entry point) ────────────────────────────────────────

export function SharePlatformRail({
  audience,
  onSelect,
  exclude,
}: {
  audience: 'personal' | 'company';
  onSelect: (platform: LinkInBioPlatform) => void;
  /** Platforms the host sheet already surfaces as its own tile. */
  exclude?: readonly LinkInBioPlatformId[];
}) {
  const { t } = useTranslation('common');
  const platforms = getLinkInBioPlatforms(audience).filter(
    (p) => !exclude?.includes(p.id),
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={railTitle}>{t('linkInBio.sectionTitle')}</span>
        <span style={railSubtitle}>
          {audience === 'company'
            ? t('linkInBio.sectionSubtitleCompany')
            : t('linkInBio.sectionSubtitle')}
        </span>
      </div>
      {/* Horizontal rail: ten platforms in a 3-up grid would push the primary
          action below the fold on a laptop. */}
      <div style={rail}>
        {platforms.map((platform) => (
          <RailTile
            key={platform.id}
            platform={platform}
            label={t(platform.labelKey)}
            onSelect={() => onSelect(platform)}
          />
        ))}
      </div>
    </div>
  );
}

function RailTile({
  platform,
  label,
  onSelect,
}: {
  platform: LinkInBioPlatform;
  label: string;
  onSelect: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={label}
      style={{
        ...railTile,
        background: hover ? 'var(--hover-strong, #e9e8ef)' : 'var(--hover, #f3f2f8)',
      }}
    >
      <span style={{ color: 'var(--text-primary)', display: 'inline-flex' }}>
        <PlatformGlyph platform={platform} size={20} />
      </span>
      <span style={railTileLabel}>{label}</span>
    </button>
  );
}

// ─── Guide (detail view) ─────────────────────────────────────────────────

export function AddToBioGuide({
  platform,
  url,
  onCopy,
  copied,
  webUrlOverride,
  onPost,
}: {
  platform: LinkInBioPlatform;
  url: string;
  onCopy: () => void;
  copied: boolean;
  /** Opens the platform's post composer. Absent when it publishes no intent. */
  onPost?: () => void;
  /**
   * A destination the caller knows to be better than the registry's generic
   * one — the CV sheet has the user's actual LinkedIn profile URL, which beats
   * `linkedin.com/in/me/`.
   */
  webUrlOverride?: string | null;
}) {
  const { t } = useTranslation('common');
  const label = t(platform.labelKey);
  const steps = readSteps(
    t as unknown as (key: string, options: { returnObjects: true }) => unknown,
    platform.stepsKey,
  );

  const destination = webUrlOverride || platform.webUrl;
  const openPlatform = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.open(destination, '_blank', 'noopener,noreferrer');
  }, [destination]);

  return (
    <div style={stack}>
      {platform.noticeKey ? (
        <div style={notice}>
          <MaterialSymbol
            name="info"
            size={18}
            style={{ color: 'var(--text-tertiary)', flexShrink: 0, marginTop: 1 }}
          />
          <span>{t(platform.noticeKey)}</span>
        </div>
      ) : null}

      {platform.prereqKey ? (
        <span style={prereq}>{t(platform.prereqKey)}</span>
      ) : null}

      <ol style={stepList}>
        {steps.map((step, index) => (
          <li key={step} style={stepRow}>
            <span style={stepIndex}>{index + 1}</span>
            <span style={stepText} dir="auto">
              {step}
            </span>
          </li>
        ))}
      </ol>

      {platform.footnoteKey ? (
        <span style={footnote}>{t(platform.footnoteKey)}</span>
      ) : null}

      <div style={linkCard}>
        <div style={linkRow}>
          <MaterialSymbol
            name="link"
            size={18}
            style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
          />
          <span style={linkCopyCol}>
            <span style={linkLabel}>{t('linkInBio.yourLink')}</span>
            <span style={linkText} dir="ltr">
              {displayUrl(url) || 'N/A'}
            </span>
          </span>
          <button
            type="button"
            onClick={onCopy}
            aria-label={t('linkInBio.copy')}
            style={copyBox}
          >
            <MaterialSymbol name={copied ? 'check' : 'content_copy'} size={16} />
          </button>
        </div>
      </div>

      <button type="button" onClick={openPlatform} style={primaryBar}>
        <span style={{ display: 'inline-flex' }}>
          <PlatformGlyph platform={platform} size={18} />
        </span>
        {t('linkInBio.goTo', { platform: label })}
      </button>

      {/* The other thing you can do with a link on this platform: announce it
          once, rather than parking it on your profile. */}
      {onPost ? (
        <button type="button" onClick={onPost} style={secondaryBar}>
          <MaterialSymbol name="edit_square" size={18} />
          {t('linkInBio.postAbout', { platform: label })}
        </button>
      ) : null}

      {/* The web destination cannot land on a field that only exists in the
          app, so say so instead of implying the button finishes the job. */}
      {platform.appOnly ? (
        <span style={{ ...footnote, textAlign: 'center' }}>
          {t('linkInBio.appOnlyHint')}
        </span>
      ) : null}
    </div>
  );
}

// ─── Styles (mirrors CvShareSheet.web.tsx) ───────────────────────────────

const FONT = "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif";

const stack: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  fontFamily: FONT,
  color: 'var(--text-primary)',
};
const railTitle: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--text-primary)',
  fontFamily: FONT,
};
const railSubtitle: CSSProperties = {
  fontSize: 13,
  lineHeight: '18px',
  color: 'var(--text-tertiary)',
  fontFamily: FONT,
};
const rail: CSSProperties = {
  display: 'flex',
  gap: 10,
  overflowX: 'auto',
  paddingBottom: 4,
  scrollSnapType: 'x proximity',
  scrollbarWidth: 'thin',
};
const railTile: CSSProperties = {
  flex: '0 0 auto',
  width: 88,
  border: 0,
  cursor: 'pointer',
  borderRadius: 8,
  padding: '14px 6px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  scrollSnapAlign: 'start',
  transition: 'background-color 140ms',
  fontFamily: FONT,
};
const railTileLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: -0.11,
  color: 'var(--text-primary)',
  textAlign: 'center',
  lineHeight: '14px',
  // Google Business Profile is three words: let it wrap rather than clip.
  overflowWrap: 'anywhere',
};
const notice: CSSProperties = {
  display: 'flex',
  gap: 10,
  padding: '12px 14px',
  borderRadius: 12,
  background: 'var(--hover, rgba(0,0,0,0.04))',
  color: 'var(--text-secondary)',
  fontSize: 13,
  lineHeight: '18px',
};
const prereq: CSSProperties = {
  fontSize: 13,
  lineHeight: '18px',
  color: 'var(--text-secondary)',
  fontWeight: 500,
};
const stepList: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};
const stepRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
};
const stepIndex: CSSProperties = {
  flexShrink: 0,
  width: 24,
  height: 24,
  borderRadius: 12,
  background: 'var(--hover, rgba(0,0,0,0.04))',
  color: 'var(--text-primary)',
  fontSize: 12,
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
};
const stepText: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  lineHeight: '22px',
  color: 'var(--text-primary)',
};
const footnote: CSSProperties = {
  fontSize: 12,
  lineHeight: '17px',
  color: 'var(--text-tertiary)',
};
const linkCard: CSSProperties = {
  border: '1px solid var(--input-border)',
  borderRadius: 16,
  padding: '8px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};
const linkRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minHeight: 52,
};
const linkCopyCol: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};
const linkLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: 'var(--text-tertiary)',
  lineHeight: '14px',
};
const linkText: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 16,
  fontWeight: 500,
};
const copyBox: CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  border: '1px solid var(--input-border)',
  cursor: 'pointer',
  background: 'transparent',
  color: 'var(--text-primary)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
const secondaryBar: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  height: 48,
  borderRadius: 9999,
  border: '1px solid var(--input-border)',
  cursor: 'pointer',
  background: 'transparent',
  color: 'var(--text-primary)',
  fontSize: 15,
  fontWeight: 600,
  fontFamily: FONT,
};
const primaryBar: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  height: 56,
  borderRadius: 9999,
  border: 0,
  cursor: 'pointer',
  background: 'var(--text-primary)',
  color: 'var(--bg)',
  fontSize: 16,
  fontWeight: 600,
  fontFamily: FONT,
};
